import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { Empty, ErrorBox, Field, Loading, Metric, PageHead, Panel, toast } from '../components/ui';
import { dateLabel, dueLabel, errorMessage, pct } from '../format';
import { useApi } from '../hooks';
import { cardState, ERROR, FACET_FIELDS, KIND, RATING, RELATION, UNDERSTANDING } from '../labels';
import { go } from '../router';
import type { Card, ItemDetail } from '../types';

export function KnowledgeDetail({ id }: { id: number }) {
  const { data, error, reload, setData } = useApi(() => api.item(id), [id]);
  const [prompt, setPrompt] = useState('');
  const [answer, setAnswer] = useState('');
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const { item, links, cards, reviews } = data;

  const act = async (fn: () => Promise<ItemDetail>, message: string) => {
    try {
      setData(await fn());
      toast(message);
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const remove = async () => {
    if (!window.confirm(`「${item.title}」을(를) 지울까요?\n카드와 복습 기록도 함께 지워집니다. (기록을 남기려면 '보관'을 쓰세요)`)) return;
    try {
      await api.deleteItem(id);
      toast('지웠습니다');
      go('/knowledge');
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const toggleCard = async (c: Card) => {
    try {
      await api.patchCard(c.id, { suspended: !c.suspended });
      reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const deleteCard = async (c: Card) => {
    if (!window.confirm('이 질문을 지울까요? 복습 기록도 함께 지워집니다.')) return;
    try {
      await api.deleteCard(c.id);
      reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const addCard = async (e: FormEvent) => {
    e.preventDefault();
    await act(() => api.addCard(id, prompt, answer), '질문을 추가했습니다');
    setPrompt('');
    setAnswer('');
  };
  const kindOf = (cardId: number) => cards.find((c) => c.id === cardId)?.kind;

  return (
    <>
      <PageHead
        title={item.title}
        sub={
          <>
            {item.domain && <span className="badge">{item.domain}</span>} {item.tags.map((t) => <span key={t} className="tag">#{t}</span>)}
            {item.archived ? <span className="badge warn">보관됨</span> : null}
          </>
        }
        actions={
          <>
            <a className="btn" href={`#/knowledge/${id}/edit`}>고치기</a>
            <button type="button" className="btn" onClick={() => void act(() => api.archiveItem(id, !item.archived), item.archived ? '복원했습니다' : '보관했습니다 (복습에서 빠짐)')}>
              {item.archived ? '복원' : '보관'}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => void remove()}>지우기</button>
          </>
        }
      />
      <div className="detail-grid">
        <div className="stack-col">
          <Panel>
            {FACET_FIELDS.map((f) =>
              item[f.key].trim() ? (
                <section key={f.key} className="facet">
                  <h3>{f.label}</h3>
                  <p className="pre">{item[f.key]}</p>
                </section>
              ) : null,
            )}
            {item.notes.trim() && (
              <section className="facet">
                <h3>메모</h3>
                <p className="pre">{item.notes}</p>
              </section>
            )}
            {(item.source_title || item.source_url || item.source_ref) && (
              <section className="facet">
                <h3>출처</h3>
                <p className="small">
                  {item.source_title}
                  {item.source_url && <> · <a href={item.source_url} target="_blank" rel="noreferrer">링크</a></>}
                  {item.source_ref && <> · <code>{item.source_ref}</code></>}
                </p>
              </section>
            )}
          </Panel>

          <Panel title="관련 개념">
            {links.length === 0 ? (
              <Empty>연결된 개념이 없습니다. '고치기'에서 연결하면 관계를 묻는 카드가 생깁니다.</Empty>
            ) : (
              <ul className="list">
                {links.map((l) => (
                  <li key={`${l.direction}-${l.id}`}>
                    <a className="list-row" href={`#/knowledge/${l.other_id}`}>
                      <span className="grow">
                        <span>{l.direction === 'in' && '← '}<b>{l.other_title}</b> <span className="badge">{RELATION[l.relation]}</span></span>
                        {l.note && <span className="muted small">{l.note}</span>}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title={`복습 카드 ${cards.length}장`}>
            <ul className="list">
              {cards.map((c) => (
                <li key={c.id} className="list-row card-row">
                  <span className="badge accent">{KIND[c.kind]}</span>
                  <span className="grow">
                    <span className="clamp-2">{c.prompt}</span>
                    <span className="muted small">
                      {cardState(c)}
                      {!c.suspended && c.reps > 0 && ` · 다음 ${dueLabel(c.due)}`}
                      {c.recall != null && ` · 지금 기억 확률 ${pct(c.recall)}`}
                      {c.reps > 0 && ` · ${c.reps}번 복습, ${c.lapses}번 잊음`}
                    </span>
                  </span>
                  <span className="row-gap">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void toggleCard(c)}>{c.suspended ? '다시 켜기' : '쉬게 하기'}</button>
                    {c.kind === 'custom' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => void deleteCard(c)}>지우기</button>}
                  </span>
                </li>
              ))}
            </ul>
            <form onSubmit={addCard} className="custom-card">
              <h3 className="sub-title">직접 질문 추가 — 새 상황에 적용하는 질문이 특히 좋습니다</h3>
              <Field label="질문" htmlFor="c-prompt">
                <input id="c-prompt" className="input" value={prompt} onChange={(e) => setPrompt(e.target.value)} required />
              </Field>
              <Field label="답" htmlFor="c-answer">
                <textarea id="c-answer" className="input" rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)} required />
              </Field>
              <button type="submit" className="btn">질문 추가</button>
            </form>
          </Panel>
        </div>

        <div className="stack-col">
          <Panel title="이해도">
            <div className="metrics-2">
              <Metric label="스스로 매긴 이해도" value={`${item.understanding_self} / 5`} sub={UNDERSTANDING[item.understanding_self]} />
              <Metric label="지금 떠올릴 확률" value={pct(item.recall)} sub={item.recall == null ? '아직 복습 전' : 'FSRS 예측, 카드 평균'} />
            </div>
          </Panel>
          <Panel title="내가 쓴 답 기록">
            {reviews.length === 0 ? (
              <Empty>복습하면 내가 쓴 답이 여기 쌓입니다 — 설명이 어떻게 나아지는지 볼 수 있습니다.</Empty>
            ) : (
              <ul className="list">
                {reviews.map((r) => (
                  <li key={r.id} className="list-row answer-log">
                    <span className="grow">
                      <span className="small">
                        <span className={`badge ${RATING[r.rating - 1].tone}`}>{RATING[r.rating - 1].label}</span>{' '}
                        {KIND[kindOf(r.card_id) ?? r.kind]} · {dateLabel(r.reviewed_at)}
                        {r.error_type && ` · ${ERROR[r.error_type]}`}
                      </span>
                      {r.typed_answer ? <span className="pre small">{r.typed_answer}</span> : <span className="muted small">(쓰지 않음)</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
